<template>
    <div>

        <PageHeader>
            <template v-if="product && product.aggregation">{{ product.aggregation.name }}</template>
            <template #side>
                <ButtonIcon :to="parentRoute">
                    {{ $t('goBack') }}
                </ButtonIcon>
            </template>
        </PageHeader>

        <Card :header="$t('product')" overlay :loading="productCardLoading">

            <ErrorMessage :value="requestProduct.getError()" />


            <div v-if="product" class="grid-template">

                <div class="label">{{ $t('id') }}:</div>
                <div class="value">{{ product.id }}</div>

                <div class="label">{{ $t('status') }}:</div>
                <div class="value">{{ product.status }}</div>

                <div class="label">{{ $t('catalogStatus') }}:</div>
                <div class="value">{{ product.catalogStatus }}</div>

                <div class="label">{{ $t('updatedAt') }}:</div>
                <div class="value">{{ $format.datetime(product.updatedAt) }}</div>

                <div class="label">{{ $t('riskScore') }}:</div>
                <div class="value">{{ product.riskScore }}</div>

                <template v-for="([key, value]) in Object.entries(product.risks || {})">
                    <div :key="`label-${key}`" class="label ml-4">{{ risks[key] || key }}:</div>
                    <div :key="`value-${key}`" class="value">{{ value }}</div>
                </template>

                <div class="label">{{ $t('aggregation') }}:</div>
                <div class="value"></div>

                <div class="label ml-4">{{ $t('id') }}:</div>
                <div class="value">{{ product.aggregation.id }}</div>

                <div class="label ml-4">{{ $t('offerId') }}:</div>
                <div class="value">{{ product.aggregation.offerId }}</div>

                <div class="label ml-4">{{ $t('name') }}:</div>
                <div class="value">{{ product.aggregation.name }}</div>

                <div class="label ml-4">{{ $t('info') }}:</div>
                <div class="value">{{ product.aggregation.info }}</div>

                <div class="label ml-4 mb-auto">{{ $t('image') }}:</div>
                <div class="value">
                    <div>{{ product.aggregation.image }}</div>
                    <ImageLoader
                        :src="product.aggregation.image"
                        alt=""
                        size="50"
                        image-height="auto"
                        class="mr-2 flex-shrink-0"
                    />
                </div>

            </div>

        </Card>

        <Card v-if="suggestion" :header="$t('catalogSuggestion')" overlay :loading="suggestionCardLoading">

            <template #nav>
                <div class="d-flex gap-2">
                    <ButtonIcon variant="success" @click="confirmCreate()">
                        {{ $t('createProduct') }}
                    </ButtonIcon>
                    <ButtonIcon variant="warning" @click="confirmIgnore()">
                        {{ $t('ignoreProposal') }}
                    </ButtonIcon>
                    <ButtonIcon variant="primary" @click="confirmEdit()">
                        {{ $t('editProposal') }}
                    </ButtonIcon>
                </div>
            </template>

            <ErrorMessage :value="requestSuggestion.getError()" />

            <b-table-simple small borderless>
                <b-thead class="thead-dark">
                    <b-tr class="border-bottom">
                        <b-th>{{ $t('name') }}</b-th>
                        <b-th>{{ $t('author') }}</b-th>
                        <b-th>{{ $t('brand') }}</b-th>
                        <b-th>{{ $t('description') }}</b-th>
                        <b-th>{{ $t('source') }}</b-th>
                    </b-tr>
                </b-thead>
                <b-tbody>
                    <b-tr>
                        <b-td>{{ suggestion.name }}</b-td>
                        <b-td>{{ suggestion.author }}</b-td>
                        <b-td>{{ suggestion.brand }}</b-td>
                        <b-td>{{ suggestion.description }}</b-td>
                        <b-td>{{ suggestion.source }}</b-td>
                    </b-tr>
                </b-tbody>
            </b-table-simple>

            <div class="my-3 d-table"></div>

            <h6>{{ $t('variants') }}</h6>
            <b-table-lite
                :items="variants"
                :fields="variantsFields"
                small
                responsive
                thead-tr-class="border-bottom"
                head-variant="secondary"
                details-td-class="border-bottom border-light"
            >
                <template #head()="scope">{{ $tx(camelCase(scope.label), scope.label) }}</template>
                <template #row-details="{item}">

                    <b-table-simple hover small responsive borderless class="m-0">
                        <b-thead>
                            <b-tr>
                                <b-th>
                                    <div class="d-flex gap-2 align-items-center accordion">
                                        <ButtonIcon
                                            v-b-toggle="`accordion-catalog-suggestion-${(item.offers || []).join('-')}`"
                                            variant="secondary"
                                            icon="chevron-down"
                                            flip-icon
                                            size="sm"
                                            class="expandable p-1"
                                            :disabled="productOffersLoading"
                                        />
                                        <span>{{ $t('offers') }}:</span>
                                        <span class="font-weight-normal">{{ item.offers.length }}</span>
                                        <span v-if="suggestionVariantsOtherOffers[item.ean]" class="font-weight-normal">(+{{ Object.values(suggestionVariantsOtherOffers[item.ean] || {}).length }})</span>
                                        <b-spinner v-if="productOffersLoading" small />
                                    </div>
                                </b-th>
                            </b-tr>
                        </b-thead>
                    </b-table-simple>
                    <b-collapse :id="`accordion-catalog-suggestion-${(item.offers || []).join('-')}`" visible>
                        <OffersTable v-if="!productOffersLoading && !productOffersError" :items="getItemOffers(item)" :fields="offersFields">
                            <template #cell(action)="data">
                                <ButtonIcon
                                    size="sm"
                                    icon="minus"
                                    variant="danger"
                                    @click="removeOfferFromVariant(item.ean, data.item)"
                                />
                            </template>
                        </OffersTable>
                        <ErrorMessage v-if="productOffersError" :value="productOffersError" :dismissible="false" />
                    </b-collapse>

                </template>
            </b-table-lite>

            <ModalEdit />

        </Card>

        <Card :header="$t('otherOffers')" overlay :loading="productOffersLoading">
            <OffersTable v-if="otherOffers.length" :items="otherOffers" :fields="offersFields">
                <template #cell(action)="data">
                    <div class="d-flex gap-1 justify-content-end">
                        <b-dropdown class="mx-1" right text="+" toggle-class="caret d-flex justify-content-center align-items-center">
                            <template #button-content>
                                <feather size="16" type="plus" />
                            </template>
                            <b-dropdown-item v-for="variant in variants" :key="variant.ean" @click="addOfferToVariant(variant.ean, data.item)">
                                {{ $t('variant') }}: {{ $t('ean') }} {{ variant.ean }}
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>
                </template>
            </OffersTable>
            <div v-else-if="!productOffersLoading">{{ $t('noOffers') }}</div>
        </Card>

    </div>
</template>



<script>
    import { CATALOG_STATUS } from '@/services/productService';
    import camelCase from 'camelcase';
    import ImageLoader from '@/components/common/ImageLoader.vue';
    import ModalEdit from './parts/ModalEdit.vue';
    import OffersTable from './parts/OffersTable.vue';

    export default {
        name: 'AggregateModerationDetails',
        components: {
            ImageLoader,
            ModalEdit,
            OffersTable,
        },
        provide() {
            return {
                parentComponent: this,
            };
        },
        data() {
            return {
                id: Number.parseInt(this.$route.params.id),
                requestProduct: this.$req('product/products'),
                requestSuggestion: this.$req('product/catalogSuggestion'),
                requestProductOffers: this.$req('product/productOffers'),
                requestTypesAndFormats: this.$req('offers/typesAndFormats'),
                requestCatalogMapping: this.$req('product/catalogMapping'),
                requestRiskDimensions: this.$req('product/RiskDimensions'),
                suggestionVariantsOtherOffers: {},
                suggestionVariantsRemovedOffers: {},
            };
        },
        computed: {
            parentRoute() {
                return this.$route.params.parentUrl ? this.$router.resolve(this.$route.params.parentUrl).resolved : this.$route.matched.slice(-2)[0];
            },
            product() {
                return this.requestProduct.getData();
            },
            suggestion() {
                return this.requestSuggestion.getData();
            },
            productOffers() {
                return this.requestProductOffers.getData();
            },
            productOffersLoading() {
                return this.requestProductOffers.getLoading();
            },
            productOffersError() {
                return this.requestProductOffers.getError();
            },
            productCardLoading() {
                return this.requestProduct.getLoading(`patch-${this.id}`) || this.requestProduct.getLoading() || this.requestCatalogMapping.getLoading();
            },
            suggestionCardLoading() {
                return this.requestProduct.getLoading(`patch-${this.id}`) || this.requestSuggestion.getLoading() || this.requestCatalogMapping.getLoading();
            },
            variants() {
                return this.suggestion?.variants?.map(item => ({ ...item, _showDetails: true })) || [];
            },
            variantsFields() {
                return Object.keys(this.suggestion?.variants[0] || {}).filter(item => item !== 'offers');
            },
            offersFields() {
                return [
                    { key: 'product', label: this.$t('aggregateOfferId') },
                    { key: 'ean', label: this.$t('ean') },
                    { key: 'name', label: this.$t('name') },
                    { key: 'info', label: this.$t('info') },
                    { key: 'code', label: this.$t('code') },
                    { key: 'feed', label: this.$t('feedShop') },
                    { key: 'type', label: this.$t('offerType') },
                    { key: 'price', label: this.$t('price') },
                    { key: 'hidden', label: this.$t('hidden') },
                    { key: 'active', label: this.$t('availability') },
                    { key: 'action', label: '' },
                ];
            },
            otherOffers() {
                const ids = [...this.suggestionVariantsOffersIds, ...this.suggestionVariantsOtherOffersIds];
                const removedIds = this.suggestionVariantsRemovedOffersIds;
                return this.productOffers?.filter(offer => !ids.includes(offer.id) || removedIds.includes(offer.id)) || [];
            },
            suggestionVariantsOtherOffersIds() {
                return this.collectOfferIds(this.suggestionVariantsOtherOffers);
            },
            suggestionVariantsRemovedOffersIds() {
                return this.collectOfferIds(this.suggestionVariantsRemovedOffers);
            },
            suggestionVariantsOffersIds() {
                return this.variants?.map(variant => variant.offers).flat(10);
            },
            offers() {
                return (this.productOffers || []).reduce((acc, offer) => ({ ...acc, [offer.id]: offer }), {});
            },
            typesAndFormats() {
                return this.requestTypesAndFormats.getData() || [];
            },
            riskDimensions() {
                return this.requestRiskDimensions.getData() || [];
            },
            risks() {
                return this.riskDimensions.reduce((acc, item) => ({ ...acc, [item.value]: item.label }), {});
            },
        },
        methods: {
            camelCase,
            collectOfferIds(offersByVariant) {
                return Object.values(offersByVariant)
                    .reduce((acc, item) => [...acc, Object.keys(item).map(id => Number.parseInt(id))], [])
                    .flat(10);
            },
            fetchProduct() {
                return this.requestProduct.get({ urlParams: { id: this.id } }).catch(() => {});
            },
            fetchSuggestion() {
                return this.requestSuggestion.get({ urlParams: { id: this.id } }).catch(() => {});
            },
            fetchProductOffers() {
                return this.requestProductOffers.get({ urlParams: { id: this.id } }).catch(() => {});
            },
            fetchTypesAndFormats() {
                return this.requestTypesAndFormats.get().catch(() => {});
            },
            confirmCreate() {
                this.$bvModal
                    .msgBoxConfirm(this.$t('confirmCreationAProductFromSuggestion'), {
                        title: this.$t('createProduct'),
                        okTitle: this.$t('create'),
                        cancelTitle: this.$t('cancel'),
                        centered: true,
                    })
                    .then(value => {
                        if (value) {
                            this.requestCatalogMapping
                                .post({
                                    urlParams: { id: this.id },
                                    requestParams: {
                                        ...this.suggestion,
                                        variants: this.suggestion.variants.reduce(
                                            (acc, variant) => [
                                                ...acc,
                                                {
                                                    ...variant,
                                                    offers: [
                                                        ...variant.offers.filter(
                                                            offerId =>
                                                                !Object.keys(this.suggestionVariantsRemovedOffers[variant.ean] || {})
                                                                    .map(id => Number(id))
                                                                    .includes(offerId)
                                                        ),
                                                        ...Object.keys(this.suggestionVariantsOtherOffers[variant.ean] || {}).map(id => Number.parseInt(id)),
                                                    ],
                                                },
                                            ],
                                            []
                                        ),
                                    },
                                })
                                .then(data => {
                                    this.$toastr.success(this.$t('dataSavedSuccessfully'));
                                    this.$router.push(this.parentRoute);
                                })
                                .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
                        }
                    });
            },
            confirmIgnore() {
                this.$bvModal
                    .msgBoxConfirm(this.$t('confirmIgnoringSuggestion'), {
                        title: this.$t('ignoreSuggestion'),
                        okTitle: this.$t('ignore'),
                        cancelTitle: this.$t('cancel'),
                        centered: true,
                    })
                    .then(value => {
                        if (value) {
                            this.requestProduct
                                .id(`patch-${this.id}`)
                                .patch({ urlParams: { id: this.id }, requestParams: { catalogStatus: CATALOG_STATUS.REJECTED } })
                                .then(data => {
                                    this.$toastr.success(this.$t('proposalHasBeenSuccessfullyIgnored'));
                                    this.$router.push(this.parentRoute);
                                })
                                .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
                        }
                    });
            },
            confirmEdit() {
                this.$root.$emit('bv::show::modal', 'modal-aggregate-moderation-product-edit');
            },
            fetchRiskDimensions() {
                return this.requestRiskDimensions.get();
            },
            addOfferToVariant(variantEan, offer) {
                if (this.suggestionVariantsRemovedOffers[variantEan]?.[offer.id]) {
                    const clone = structuredClone(this.suggestionVariantsRemovedOffers[variantEan] || {});
                    delete clone[offer.id];
                    this.$set(this.suggestionVariantsRemovedOffers, variantEan, clone);
                } else {
                    this.$set(this.suggestionVariantsOtherOffers, variantEan, { ...(this.suggestionVariantsOtherOffers[variantEan] || {}), [offer.id]: offer });
                }
            },
            removeOfferFromVariant(variantEan, offer) {
                if (this.suggestionVariantsOtherOffers[variantEan]?.[offer.id]) {
                    const clone = structuredClone(this.suggestionVariantsOtherOffers[variantEan] || {});
                    delete clone[offer.id];
                    this.$set(this.suggestionVariantsOtherOffers, variantEan, clone);
                } else {
                    this.$set(this.suggestionVariantsRemovedOffers, variantEan, { ...(this.suggestionVariantsRemovedOffers[variantEan] || {}), [offer.id]: offer });
                }
            },
            getItemOffers(item) {
                return [
                    ...item.offers
                        .map(id => this.offers[id])
                        .filter(offer => !!offer)
                        .filter(
                            offer =>
                                !Object.keys(this.suggestionVariantsRemovedOffers[offer.ean] || {})
                                    .map(id => Number(id))
                                    .includes(offer.id)
                        ),
                    ...Object.values(this.suggestionVariantsOtherOffers[item.ean] || {}),
                ];
            },
        },
        created() {
            this.fetchProduct();
            this.fetchSuggestion();
            this.fetchProductOffers();
            this.fetchRiskDimensions();
        },
    };
</script>



<style lang="scss" scoped>
    .grid-template {
        display: grid;
        grid-template-columns: minmax(60px, max-content) auto;
        column-gap: 1rem;
        row-gap: 0.4rem;
        align-items: center;

        .label {
            font-weight: bold;
        }
    }
</style>
